import { useEffect } from "react";
import { useSelector } from "react-redux";
import { CheckCircle as CheckCircleIcon } from "@remo-co/ui-core/src/icons/CheckCircle";
import { InfoCircle as InfoCircleIcon } from "@remo-co/ui-core/src/icons/InfoCircle";
import { AlertCircle as AlertCircleIcon } from "@remo-co/ui-core/src/icons/AlertCircle";
import { XCircle as XCircleIcon } from "@remo-co/ui-core/src/icons/XCircle";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Close as CloseIcon } from "@remo-co/ui-core/src/icons/Close";
import useNotificationActions from "../../hooks/useNotificationActions";
import { selectNotifications } from "../../redux/selectors";
import {
  getBorderColor,
  getLevelBackgroundColor,
  getPosition,
  styles,
} from "./style";

const NotificationContainer = (): JSX.Element | null => {
  const notifications = useSelector(selectNotifications);

  const { removeNotification } = useNotificationActions();

  useEffect(() => {
    const timeoutIds: { [key: string]: NodeJS.Timeout } = {};

    notifications.forEach((notification) => {
      if (notification.autoDismiss && notification.uid) {
        timeoutIds[notification.uid] = setTimeout(() => {
          removeNotification(notification.uid!);
        }, notification.autoDismiss * 1000);
      }
    });

    return () => {
      Object.values(timeoutIds).forEach(clearTimeout);
    };
  }, [notifications, removeNotification]);

  const handleRemove = (uid: string | undefined) => {
    if (uid) {
      removeNotification(uid);
    }
  };

  const getIcon = (level?: string) => {
    switch (level) {
      case "error":
        return (
          <Box sx={styles.errorIcon}>
            <XCircleIcon />
          </Box>
        );
      case "warning":
        return (
          <Box sx={styles.warningIcon}>
            <AlertCircleIcon />
          </Box>
        );
      case "info":
        return (
          <Box sx={styles.infoIcon}>
            <InfoCircleIcon />
          </Box>
        );
      case "success":
      default:
        return (
          <Box sx={styles.successIcon}>
            <CheckCircleIcon />
          </Box>
        );
    }
  };

  if (!notifications.length) {
    return null;
  }

  return (
    <Box sx={styles.container}>
      {notifications.map((notification) => {
        const positionStyle = getPosition(notification.position);

        return (
          <Box
            key={notification.uid}
            sx={{
              ...styles.notificationItem,
              ...positionStyle,
              border: notification.level
                ? getBorderColor(notification.level)
                : "1px solid #a6e9f2",
            }}
            data-testid="notification-container"
          >
            {notification.showIcon !== false && (
              <Box
                sx={{
                  ...styles.notificationLevel,
                  backgroundColor: notification.level
                    ? getLevelBackgroundColor(notification.level)
                    : "#e5f8fb",
                }}
              >
                {getIcon(notification.level)}
              </Box>
            )}
            <Box sx={styles.notificationContent}>
              {notification.title && (
                <Typography variant="h4">{notification.title}</Typography>
              )}
              {notification.message && (
                <Typography variant="body1">{notification.message}</Typography>
              )}
              {notification.action && (
                <Button
                  type="button"
                  sx={styles.notificationAction}
                  onClick={() => {
                    notification.action?.callback?.();
                    handleRemove(notification.uid);
                  }}
                >
                  {notification.action.label}
                </Button>
              )}
            </Box>
            {notification.dismissible !== false && (
              <Box sx={styles.closeContainer}>
                <CloseIcon
                  sx={styles.closeIcon}
                  onClick={() => handleRemove(notification.uid)}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default NotificationContainer;
